import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { runDatasetQuery } from "@/lib/reporting/engine.functions";
import { getLatestReconciliationStatus } from "@/lib/inventory-reconciliation.functions";
import { getLowStock } from "@/lib/inventory-views.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { KpiCard } from "@/components/reporting/kpi-card";
import { ChartCard } from "@/components/reporting/chart-card";
import { ReconCard } from "@/components/reporting/recon-card";
import { Card, CardContent } from "@/components/ui/card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { Boxes, Layers, AlertTriangle, Warehouse, ArrowRight } from "lucide-react";
import { useMemo } from "react";

export const Route = createFileRoute("/_authenticated/app/reports/inventory")({ component: InventoryDash });

const STATE_COLORS: Record<string, string> = {
  available: "var(--primary)",
  allocated: "var(--muted-foreground)",
  quarantine: "var(--destructive)",
  damaged: "var(--destructive)",
  hold: "var(--accent-foreground)",
  picked: "var(--secondary-foreground)",
};

function InventoryDash() {
  const { tenantId, facilityId, customerId, tenants } = useScope();
  const run = useServerFn(runDatasetQuery);
  const fetchRecon = useServerFn(getLatestReconciliationStatus);
  const fetchLowStock = useServerFn(getLowStock);
  const tenantName = tenants.find((t) => t.id === tenantId)?.name ?? null;

  const { data: byState, isLoading } = useQuery({
    queryKey: ["rpt-inv-state", tenantId],
    enabled: !!tenantId,
    queryFn: () => run({ data: {
      tenantId: tenantId!, datasetKey: "inventory_balances",
      group: ["state"],
      aggregations: [{ column: "qty", fn: "sum", alias: "qty" }, { column: "sku_id", fn: "count", alias: "lines" }],
      limit: 200, offset: 0,
    }}),
  });

  const { data: byFacility } = useQuery({
    queryKey: ["rpt-inv-fac", tenantId],
    enabled: !!tenantId,
    queryFn: () => run({ data: {
      tenantId: tenantId!, datasetKey: "inventory_balances",
      group: ["facility_id"],
      aggregations: [{ column: "qty", fn: "sum", alias: "qty" }],
      limit: 50, offset: 0,
    }}),
  });

  const { data: recon } = useQuery({
    queryKey: ["rpt-inv-recon", tenantId],
    enabled: !!tenantId,
    refetchInterval: 5 * 60_000,
    queryFn: () => fetchRecon({ data: { tenantId: tenantId! } }),
  });

  const { data: lowStock } = useQuery({
    queryKey: ["rpt-inv-low-stock", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => fetchLowStock({ data: {
      tenantId: tenantId!, facilityId: facilityId!,
      customerId: customerId ?? null,
    }}),
  });
  const lowStockCount = lowStock?.length ?? 0;

  const totals = useMemo(() => {
    const rows = byState?.rows ?? [];
    let total = 0, available = 0, alloc = 0, quar = 0;
    for (const r of rows) {
      const q = Number(r.qty ?? 0); total += q;
      if (r.state === "available") available = q;
      if (r.state === "allocated") alloc = q;
      if (r.state === "quarantine" || r.state === "damaged") quar += q;
    }
    return { total, available, alloc, quar };
  }, [byState]);

  const stateData = (byState?.rows ?? []).map((r: any) => ({
    state: r.state ?? "—", qty: Number(r.qty ?? 0),
  }));
  const facData = (byFacility?.rows ?? [])
    .map((r: any) => ({ facility: String(r.facility_id ?? "—").slice(0, 8), qty: Number(r.qty ?? 0) }))
    .sort((a, b) => b.qty - a.qty).slice(0, 10);

  return (
    <>
      <PageHeader title="Inventory" description="On-hand by state and facility." />
      <div className="p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Total on hand" value={totals.total} icon={<Boxes className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Available" value={totals.available} icon={<Layers className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Allocated" value={totals.alloc} icon={<Warehouse className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Quarantine / damaged" value={totals.quar} icon={<AlertTriangle className="h-4 w-4" />} loading={isLoading} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <ReconCard recon={recon} tenantId={tenantId} tenantName={tenantName} />
        </div>
        {lowStockCount > 0 && (
          <Link to="/app/inventory/low-stock" className="block group">
            <Card className="border-warning/40 bg-warning/5 hover:bg-warning/10 transition-colors">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-md bg-warning/15 text-warning flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-foreground">
                    {lowStockCount} SKU{lowStockCount === 1 ? "" : "s"} below reorder point
                  </div>
                  <div className="text-xs text-muted-foreground">Review and replenish to avoid stockouts.</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </CardContent>
            </Card>
          </Link>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Inventory by State">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stateData} dataKey="qty" nameKey="state" outerRadius={90} label>
                  {stateData.map((d, i) => (
                    <Cell key={i} fill={STATE_COLORS[d.state] ?? "var(--muted)"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Top Facilities by Qty">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={facData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" fontSize={11} />
                <YAxis type="category" dataKey="facility" fontSize={11} width={80} />
                <Tooltip />
                <Bar dataKey="qty" fill="var(--primary)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </>
  );
}